/**
 * Convención de eficiencia ACTIVA en este navegador.
 *
 * Es la contraparte del portal de `packages/stats/src/efficiency.ts` (las
 * "fórmulas redefinibles"): cada métrica publicada es `(Σ plus − Σ minus) /
 * total`, y un `EfficiencyConfig` define qué evaluaciones suman, restan o se
 * ignoran en `efficiency`/`perfectPct`/`errorPct`, con el rótulo que la UI
 * muestra. El portal NO importa `@scout/stats` (ver `log.ts`): el modelo y los
 * dos presets se copian acá y `test/parity-stats.test.ts` es lo que impide que
 * las dos copias se despeguen.
 *
 * ## Dónde vive la elección
 *
 * En los settings del portal (`lib/settings.ts`, campo `efficiencyPreset`):
 * es una preferencia de LECTURA de quien mira la pantalla, no un hecho del
 * partido — el log guarda evaluaciones, nunca porcentajes. Al API viaja como
 * `?efficiency=<id>` en los reportes (`/report`, `/report.html`,
 * `/season/report`), y a las proyecciones locales como el objeto entero.
 *
 * Sólo PRESETS con id, a propósito: el contrato admite convenciones custom,
 * pero un editor de fórmulas es una pantalla entera y el API valida ids en el
 * borde (un objeto malformado daría NaN silencioso en stats). El día que un
 * club traiga su convención, es un preset más en las DOS copias.
 *
 * El estado activo vive en un store observable de módulo (mismo patrón que el
 * idioma en `i18n/index.ts`): los settings lo escriben al cargar y al guardar,
 * los componentes lo leen con `useSyncExternalStore` y se re-renderizan solos.
 */
import type { Evaluation, EvaluationCounts } from './log';

/** `(Σ plus − Σ minus) / total`; lo que no aparece en ninguna lista se ignora. */
export interface MetricFormula {
  /** Cómo se llama la métrica en pantalla (ej. "Positividad", "Kill %"). */
  label: string;
  plus: readonly Evaluation[];
  minus: readonly Evaluation[];
}

/** Las tres métricas de un fundamento: los tres ratios del `SkillLine`. */
export interface SkillFormulas {
  efficiency: MetricFormula;
  perfectPct: MetricFormula;
  errorPct: MetricFormula;
}

/** La convención completa de un cuerpo técnico (misma forma que en stats). */
export interface EfficiencyConfig {
  id: string;
  name: string;
  base: SkillFormulas;
  bySkill?: Partial<Record<string, Partial<SkillFormulas>>>;
}

/** Evalúa una fórmula sobre los conteos. División protegida: total 0 ⇒ 0. */
export function metricOf(formula: MetricFormula, counts: EvaluationCounts, total: number): number {
  if (!Number.isFinite(total) || total === 0) return 0;
  let numerator = 0;
  for (const e of formula.plus) numerator += counts[e];
  for (const e of formula.minus) numerator -= counts[e];
  return numerator / total;
}

/** Las tres fórmulas efectivas de un fundamento: override sobre `base`. */
export function formulasFor(skill: string, config: EfficiencyConfig): SkillFormulas {
  const override = config.bySkill?.[skill];
  if (!override) return config.base;
  return {
    efficiency: override.efficiency ?? config.base.efficiency,
    perfectPct: override.perfectPct ?? config.base.perfectPct,
    errorPct: override.errorPct ?? config.base.errorPct,
  };
}

const GENERIC_FORMULAS: SkillFormulas = {
  efficiency: { label: 'Eficiencia', plus: ['#'], minus: ['='] },
  perfectPct: { label: 'Perfectas %', plus: ['#'], minus: [] },
  errorPct: { label: 'Error %', plus: ['='], minus: [] },
};

/** Preset `estandar`: transcripción 1:1 de las fórmulas de siempre. */
export const STANDARD_EFFICIENCY: EfficiencyConfig = {
  id: 'estandar',
  name: 'Estándar (DataVolley)',
  base: GENERIC_FORMULAS,
  bySkill: {
    attack: {
      efficiency: { label: 'Eficiencia', plus: ['#'], minus: ['=', '/'] },
      perfectPct: { label: 'Kill %', plus: ['#'], minus: [] },
    },
    serve: {
      perfectPct: { label: 'Ace %', plus: ['#'], minus: [] },
    },
    reception: {
      perfectPct: { label: 'Positividad', plus: ['#', '+'], minus: [] },
    },
  },
};

/** Preset `positividad-estricta`: la recepción no cuenta el `+` como positivo. */
export const STRICT_POSITIVITY_EFFICIENCY: EfficiencyConfig = {
  id: 'positividad-estricta',
  name: 'Positividad estricta (el + no suma)',
  base: GENERIC_FORMULAS,
  bySkill: {
    attack: STANDARD_EFFICIENCY.bySkill?.['attack'] ?? {},
    serve: STANDARD_EFFICIENCY.bySkill?.['serve'] ?? {},
    // Sin override de recepción: cae en la base `# / N`, que es el punto.
  },
};

/** Presets con nombre, por id — el mismo catálogo que publica `@scout/stats`. */
export const EFFICIENCY_PRESETS: Readonly<Record<string, EfficiencyConfig>> = {
  [STANDARD_EFFICIENCY.id]: STANDARD_EFFICIENCY,
  [STRICT_POSITIVITY_EFFICIENCY.id]: STRICT_POSITIVITY_EFFICIENCY,
};

/** Ids en orden estable, para el selector de Ajustes. */
export const EFFICIENCY_PRESET_IDS: readonly string[] = Object.keys(EFFICIENCY_PRESETS);

export const DEFAULT_EFFICIENCY_PRESET = STANDARD_EFFICIENCY.id;

export function isEfficiencyPresetId(value: unknown): value is string {
  return typeof value === 'string' && Object.prototype.hasOwnProperty.call(EFFICIENCY_PRESETS, value);
}

// ---------------------------------------------------------------------------
// Store de la convención activa (observable, fuera de React)
// ---------------------------------------------------------------------------

let current: string = DEFAULT_EFFICIENCY_PRESET;
const listeners = new Set<() => void>();

export function getEfficiencyPreset(): string {
  return current;
}

/** La config activa entera, lista para pasarle a las proyecciones locales. */
export function getEfficiencyConfig(): EfficiencyConfig {
  return EFFICIENCY_PRESETS[current] ?? STANDARD_EFFICIENCY;
}

/** Cambia la convención en caliente y avisa a la UI (igual que `setLocale`). */
export function setEfficiencyPreset(id: string): void {
  const next = isEfficiencyPresetId(id) ? id : DEFAULT_EFFICIENCY_PRESET;
  if (next === current) return;
  current = next;
  for (const listener of listeners) listener();
}

export function subscribeEfficiency(listener: () => void): () => void {
  listeners.add(listener);
  return () => void listeners.delete(listener);
}
